/**
 * @file Sol et ambiance du biome : plan colore a taches (texture canvas generee une fois par biome), bordure d'arene, brouillard/fond ; portee GPU 'biome'.
 */
import {
  BufferAttribute,
  BoxGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  CylinderGeometry,
  DoubleSide,
  DynamicDrawUsage,
  Fog,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshToonMaterial,
  PlaneGeometry,
  Quaternion,
  RepeatWrapping,
  SRGBColorSpace,
  TorusGeometry,
  Vector3,
} from 'three';
import type { BufferGeometry, Material, Object3D, Scene } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { BiomeId } from '@shared/enums';
import type { ArenaDef, BiomeDef } from '@config/types';
import { RENDER } from '@config/index';

/** Ce que le sol lit d'un biome ; `id` = `hub:<saison>` pour le decor de saison du hub (voir GameRenderer). */
export type GroundLook = Pick<BiomeDef, 'ground' | 'sky' | 'fog'> & { id: string };
import { shapeRng } from '../factories/TargetFactory';
import type { ResourceTracker } from '../gpu/ResourceTracker';
import type { ToonKit } from '../toon/toon';

const SHADOW_CAPACITY = 240;
const SHADOW_OPACITY = RENDER.lighting.contactShadowOpacity;
const SHADOW_COLOR = RENDER.toon.shadowTint;
const GROUND_SIZE = 240;
const GROUND_TEX = 512;
const GROUND_REPEAT = 10;
const BORDER_COLOR = '#fff3dd';
const BORDER_TUBE = 0.18;
const BARRIER_FULL = '#ff9ec7';
const BARRIER_LOW = '#7a2e44';
const BARRIER_HEIGHT = 1.1;

// ---- Chemin en oblique (arene 'corridor') : falaise montante a gauche (plateau), a-pic a droite (contrebas), comme l'original.
/** Periode du motif (= periode de la texture de sol : GROUND_SIZE / GROUND_REPEAT) : le decor suit la camera par pas entiers. */
const PATH_PERIOD = GROUND_SIZE / GROUND_REPEAT;
/** Longueur des bandes du chemin (centrees sur la camera). */
const PATH_LENGTH = PATH_PERIOD * 15;
/** Le terrain en haut a gauche du chemin est PLUS HAUT (plateau), celui en bas a droite PLUS BAS (contrebas). */
const PLATEAU_HEIGHT = 4.2;
const DROP_DEPTH = 6;
const SIDE_WIDTH = 160;
const CLIFF_DEPTH = 1.6;
/** Couleurs de roche par biome (face eclairee, ombre). */
const CLIFF_COLORS: Record<BiomeId, { rock: string; dark: string }> = {
  forest: { rock: '#b98f62', dark: '#6a4a33' },
  desert: { rock: '#d2b98f', dark: '#ae9169' },
  tundra: { rock: '#aebccb', dark: '#8a99ab' },
  autumn: { rock: '#b08a6a', dark: '#85654a' },
  blossom: { rock: '#b9a2a8', dark: '#927b83' },
};
/** Plateau : herbe un peu plus claire (en pleine lumiere) ; contrebas : nettement plus sombre (dans l'ombre de la falaise). */
const PLATEAU_TINT = '#f4ffe9';
const LOWLAND_TINT = '#7f8c72';

/**
 * - setBiome(def) : si biome different -> res.release('biome') puis : scene.background = Color(def.sky), scene.fog = Fog(def.fog...) ;
 *   sol = PlaneGeometry avec texture CanvasTexture 512x512 (fond ground.base, taches ground.patch, touffes ground.accent), repetee ;
 *   materiau toon ; tout tracke en portee 'biome'.
 * - setArena(arena) : bordure visuelle (anneau flat pour un cercle, 4 boites basses pour un rect) ; release('mode') au changement
 *   de mode. Mode Monster : cylindre/anneau de barriere rose au rayon barrierRadius dont l'opacite/teinte suit hud.barrier (setBarrier).
 * - Ombres de contact : disque sombre transparent sous chaque chat/cible (InstancedMesh partage, capacite 160) via drawShadow().
 * - Brouillard : les distances de la config sont relatives au point cadre (`setViewDistance` les decale de la distance camera).
 * - dispose() : release('biome') + release('mode').
 */
export class GroundRenderer {
  private biomeId = '';
  private fog: Fog | null = null;
  private fogNear = 40;
  private fogFar = 90;
  private viewDistance = 0;
  private ground: Mesh | null = null;
  private groundTexture: CanvasTexture | null = null;
  private readonly arenaRoot = new Group();
  /** Decor du chemin (sol, plateau, contrebas, falaises) : suit la camera par pas de PATH_PERIOD. */
  private readonly pathRoot = new Group();
  private arena: ArenaDef | null = null;
  /** Couleur de base du sol du biome : le haut des falaises prolonge le plateau (gauche) et le chemin (droite). */
  private groundColor = '#a8dc8c';
  /** Materiaux teintes du plateau / contrebas (portee 'biome', crees une fois par biome). */
  private sideMats: { plateau: Material; low: Material } | null = null;
  private barrierMaterial: MeshBasicMaterial | null = null;
  private barrierWall: MeshBasicMaterial | null = null;
  /** Geometries d'arene mises en cache par parametres (bornees par le nombre de modes) : pas de fuite au changement de mode. */
  private readonly arenaGeo = new Map<string, BufferGeometry>();
  private readonly shadows: InstancedMesh;
  private shadowCount = 0;
  private readonly m = new Matrix4();
  private readonly q = new Quaternion();
  private readonly p = new Vector3();
  private readonly s = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly res: ResourceTracker,
  ) {
    scene.add(this.arenaRoot, this.pathRoot);
    const geo = res.track(new CircleGeometry(1, 20));
    geo.rotateX(-Math.PI / 2);
    const mat = res.track(
      new MeshBasicMaterial({ color: SHADOW_COLOR, transparent: true, opacity: SHADOW_OPACITY, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }),
    );
    this.shadows = new InstancedMesh(geo, mat, SHADOW_CAPACITY);
    this.shadows.instanceMatrix.setUsage(DynamicDrawUsage);
    this.shadows.frustumCulled = false;
    this.shadows.count = 0;
    this.shadows.renderOrder = 1;
    scene.add(this.shadows);
  }

  setBiome(def: GroundLook): void {
    if (def.id === this.biomeId) return;
    this.biomeId = def.id;
    this.groundColor = def.ground.base;
    if (this.ground) {
      this.scene.remove(this.ground);
      this.ground = null;
    }
    this.sideMats = null;
    this.res.release('biome');

    this.scene.background = new Color(def.sky);
    this.fogNear = def.fog.near;
    this.fogFar = def.fog.far;
    this.fog = new Fog(def.fog.color, this.viewDistance + this.fogNear * 0.5, this.viewDistance + this.fogFar * 0.7);
    this.scene.fog = this.fog;

    const texture = this.res.track(this.makeGroundTexture(def), 'biome');
    this.groundTexture = texture;
    const geometry = this.res.track(new PlaneGeometry(GROUND_SIZE, GROUND_SIZE), 'biome');
    geometry.rotateX(-Math.PI / 2);
    const material = this.res.track(new MeshToonMaterial({ map: texture, gradientMap: this.kit.gradient }), 'biome');
    const ground = new Mesh(geometry, material);
    ground.renderOrder = -2;
    ground.matrixAutoUpdate = false;
    ground.updateMatrix();
    this.scene.add(ground);
    this.ground = ground;
    if (this.arena?.shape === 'corridor') this.buildCorridor(this.arena);
  }

  /** Point vise par la camera (Three x, z) : le decor du chemin se recale par pas entiers de la periode (raccord invisible). */
  follow(_x: number, z: number): void {
    if (this.pathRoot.children.length === 0) return;
    this.pathRoot.position.z = Math.round(z / PATH_PERIOD) * PATH_PERIOD;
  }

  /** Distance camera -> point cadre : decale le brouillard (les valeurs de la config sont relatives au plan de jeu). */
  setViewDistance(d: number): void {
    if (Math.abs(d - this.viewDistance) < 0.01) return;
    this.viewDistance = d;
    if (this.fog) {
      this.fog.near = d + this.fogNear * 0.5;
      this.fog.far = d + this.fogFar * 0.7;
    }
  }

  setArena(arena: ArenaDef, barrierRadius: number | null): void {
    this.clearArena();
    this.res.release('mode');
    this.arena = arena;
    this.barrierMaterial = null;
    this.barrierWall = null;
    const mat = this.kit.toon(BORDER_COLOR);

    if (arena.shape === 'corridor') {
      this.buildCorridor(arena);
    } else if (arena.shape === 'circle') {
      const r = arena.halfW;
      const geo = this.cached(`ring:${r}`, () => {
        const g = new TorusGeometry(r, BORDER_TUBE, 8, Math.max(48, Math.round(r * 8)));
        g.rotateX(Math.PI / 2);
        return g;
      });
      this.addMesh(geo, mat, 0, BORDER_TUBE * 0.6, 0, true);
    } else {
      const { halfW: w, halfH: h } = arena;
      const t = BORDER_TUBE * 2;
      const lenX = this.cached(`wallX:${w}`, () => new BoxGeometry(w * 2 + t * 2, t, t));
      const lenZ = this.cached(`wallZ:${h}`, () => new BoxGeometry(t, t, h * 2));
      this.addMesh(lenX, mat, 0, t / 2, h + t / 2, true);
      this.addMesh(lenX, mat, 0, t / 2, -h - t / 2, true);
      this.addMesh(lenZ, mat, w + t / 2, t / 2, 0, true);
      this.addMesh(lenZ, mat, -w - t / 2, t / 2, 0, true);
    }

    if (barrierRadius !== null) {
      this.barrierMaterial = this.res.track(new MeshBasicMaterial({ color: BARRIER_FULL, transparent: true, opacity: 0.85, depthWrite: false }), 'mode');
      this.barrierWall = this.res.track(new MeshBasicMaterial({ color: BARRIER_FULL, transparent: true, opacity: 0.25, depthWrite: false, side: DoubleSide }), 'mode');
      const ring = this.cached(`barrier:${barrierRadius}`, () => {
        const g = new TorusGeometry(barrierRadius, 0.22, 8, Math.max(64, Math.round(barrierRadius * 10)));
        g.rotateX(Math.PI / 2);
        return g;
      });
      const wall = this.cached(`barrierWall:${barrierRadius}`, () => new CylinderGeometry(barrierRadius, barrierRadius, BARRIER_HEIGHT, 48, 1, true));
      this.addMesh(ring, this.barrierMaterial, 0, 0.25, 0, false);
      this.addMesh(wall, this.barrierWall, 0, BARRIER_HEIGHT / 2, 0, false);
    }
  }

  /** Teinte / opacite de la barriere selon ses PV (1 = pleine). */
  setBarrier(hp01: number): void {
    const k = Math.min(1, Math.max(0, hp01));
    if (this.barrierMaterial) {
      this.barrierMaterial.color.set(BARRIER_LOW).lerp(new Color(BARRIER_FULL), k);
      this.barrierMaterial.opacity = 0.35 + 0.5 * k;
    }
    if (this.barrierWall) {
      this.barrierWall.color.set(BARRIER_LOW).lerp(new Color(BARRIER_FULL), k);
      this.barrierWall.opacity = 0.08 + 0.2 * k;
    }
  }

  beginShadows(): void {
    this.shadowCount = 0;
  }

  drawShadow(x: number, y: number, radius: number): void {
    if (this.shadowCount >= SHADOW_CAPACITY) return;
    this.p.set(x, 0.03, -y);
    this.s.set(radius, 1, radius);
    this.m.compose(this.p, this.q, this.s);
    this.shadows.setMatrixAt(this.shadowCount++, this.m);
  }

  endShadows(): void {
    this.shadows.count = this.shadowCount;
    this.shadows.visible = this.shadowCount > 0;
    this.shadows.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.clearArena();
    this.scene.remove(this.pathRoot);
    if (this.ground) this.scene.remove(this.ground);
    this.ground = null;
    this.scene.remove(this.arenaRoot, this.shadows);
    this.shadows.dispose();
    this.res.release('biome');
    this.res.release('mode');
    this.scene.fog = null;
    this.scene.background = null;
    this.arenaGeo.clear();
    this.biomeId = '';
  }

  // ------------------------------------------------------------------------------------------------------------------

  private cached(key: string, make: () => BufferGeometry): BufferGeometry {
    let g = this.arenaGeo.get(key);
    if (!g) {
      g = this.res.track(make());
      this.arenaGeo.set(key, g);
    }
    return g;
  }

  private addMesh(geometry: BufferGeometry, material: Material, x: number, y: number, z: number, outline: boolean): void {
    const mesh = new Mesh(geometry, material);
    mesh.position.set(x, y, z);
    if (outline) mesh.add(this.kit.outlineFor(geometry, 2));
    this.arenaRoot.add(mesh);
  }

  private clearArena(): void {
    const kids: Object3D[] = [...this.arenaRoot.children, ...this.pathRoot.children];
    for (const k of kids) k.removeFromParent();
    if (this.ground) this.ground.visible = true;
  }

  /**
   * Chemin : bande de sol (texture du biome, UV en coordonnees monde), plateau a gauche en hauteur, contrebas a droite,
   * falaises en dalles irregulieres (toon + contour). Geometries en cache par biome/largeur ; materiaux de sol en portee 'biome'.
   */
  private buildCorridor(arena: ArenaDef): void {
    for (const k of [...this.pathRoot.children]) k.removeFromParent();
    const tex = this.groundTexture;
    if (!tex || !this.ground) return;
    this.ground.visible = false;
    const w = arena.halfW;
    const biome = (this.biomeId || 'forest') as BiomeId;
    const colors = CLIFF_COLORS[biome] ?? CLIFF_COLORS.forest;
    const pathMat = this.ground.material as Material;
    this.sideMats ??= {
      plateau: this.res.track(new MeshToonMaterial({ map: tex, color: new Color(PLATEAU_TINT), gradientMap: this.kit.gradient }), 'biome'),
      low: this.res.track(new MeshToonMaterial({ map: tex, color: new Color(LOWLAND_TINT), gradientMap: this.kit.gradient }), 'biome'),
    };
    const plateauMat = this.sideMats.plateau;
    const lowMat = this.sideMats.low;

    const floor = this.cached(`path:${w}`, () => this.strip(-w - 0.5, w + 0.5));
    const plateau = this.cached(`plateau:${w}`, () => this.strip(-w - SIDE_WIDTH, -w - 0.15 - CLIFF_DEPTH * 0.6));
    const low = this.cached(`low:${w}`, () => this.strip(w + 0.15 + CLIFF_DEPTH * 0.6, w + SIDE_WIDTH));
    this.addPath(floor, pathMat, 0, false, -2);
    this.addPath(plateau, plateauMat, PLATEAU_HEIGHT, false, -2);
    this.addPath(low, lowMat, -DROP_DEPTH, false, -2);

    const rockMat = this.kit.toon('#ffffff', { vertexColors: true });
    // Gauche : paroi qui MONTE du chemin jusqu'au plateau (dessus = herbe du plateau, bord legerement irregulier).
    const plateauTop = new Color(this.groundColor).multiply(new Color(PLATEAU_TINT));
    const left = this.cached(`cliffL:${w}:${biome}`, () => this.cliff(-w - 0.15, -1, 0, PLATEAU_HEIGHT, 0.22, colors, plateauTop, 11));
    // Droite : a-pic qui DESCEND du bord du chemin vers le contrebas (dessus affleurant, couleur du chemin, sous la bande de sol).
    const right = this.cached(`cliffR:${w}:${biome}`, () => this.cliff(w + 0.15, 1, -DROP_DEPTH, -0.03, 0, colors, new Color(this.groundColor), 23));
    this.addPath(left, rockMat, 0, true, 0);
    this.addPath(right, rockMat, 0, true, 0);
  }

  private addPath(geometry: BufferGeometry, material: Material, y: number, outline: boolean, order: number): void {
    const mesh = new Mesh(geometry, material);
    mesh.position.y = y;
    mesh.renderOrder = order;
    if (outline) mesh.add(this.kit.outlineFor(geometry, 2));
    this.pathRoot.add(mesh);
  }

  /** Bande horizontale x0..x1 sur PATH_LENGTH, UV = coordonnees monde (meme echelle que le grand sol : raccord exact). */
  private strip(x0: number, x1: number): BufferGeometry {
    const g = new PlaneGeometry(x1 - x0, PATH_LENGTH);
    g.rotateX(-Math.PI / 2);
    g.translate((x0 + x1) / 2, 0, 0);
    const pos = g.getAttribute('position');
    const uv = g.getAttribute('uv');
    for (let i = 0; i < pos.count; i++) uv.setXY(i, pos.getX(i) / GROUND_SIZE + 0.5, -pos.getZ(i) / GROUND_SIZE + 0.5);
    uv.needsUpdate = true;
    return g;
  }

  /**
   * Falaise en dalles : face interieure en `face` (cote chemin), dalles vers l'exterieur (`dir` = -1 gauche, +1 droite), du bas `bottom`
   * au sommet `top` (+ variations). Motif de periode PATH_PERIOD repete sur PATH_LENGTH ; couleur par sommet (face / ombre / dessus).
   */
  private cliff(
    face: number,
    dir: -1 | 1,
    bottom: number,
    top: number,
    topJitter: number,
    colors: { rock: string; dark: string },
    topColor: Color,
    seed: number,
  ): BufferGeometry {
    const rand = shapeRng(seed);
    const pattern: Array<{ z: number; len: number; dx: number; top: number; shade: number }> = [];
    for (let z = 0; z < PATH_PERIOD - 0.5; ) {
      const len = Math.min(PATH_PERIOD - z, 2.6 + rand() * 2.2);
      pattern.push({ z: z + len / 2, len: len + 0.25, dx: (rand() - 0.5) * 0.5, top: top + rand() * topJitter, shade: rand() });
      z += len;
    }
    const parts: BufferGeometry[] = [];
    const rock = new Color(colors.rock);
    const dark = new Color(colors.dark);
    const topC = topColor;
    const c = new Color();
    const reps = Math.round(PATH_LENGTH / PATH_PERIOD);
    for (let r = 0; r < reps; r++) {
      const z0 = -PATH_LENGTH / 2 + r * PATH_PERIOD;
      for (const s of pattern) {
        const h = s.top - bottom;
        const box = new BoxGeometry(CLIFF_DEPTH, h, s.len);
        box.translate(face + dir * (CLIFF_DEPTH / 2) - dir * s.dx, bottom + h / 2, -(z0 + s.z));
        const pos = box.getAttribute('position');
        const nrm = box.getAttribute('normal');
        const col = new Float32Array(pos.count * 3);
        for (let i = 0; i < pos.count; i++) {
          if (nrm.getY(i) > 0.5) c.copy(topC);
          else c.copy(rock).lerp(dark, 0.25 + s.shade * 0.45 + (nrm.getZ(i) !== 0 ? 0.2 : 0));
          col.set([c.r, c.g, c.b], i * 3);
        }
        box.setAttribute('color', new BufferAttribute(col, 3));
        parts.push(box);
      }
    }
    const merged = mergeGeometries(parts, false);
    for (const part of parts) part.dispose();
    return merged ?? new BoxGeometry(0.01, 0.01, 0.01);
  }

  /**
   * Details du sol de saison du hub (une seule fois par texture) : printemps petites fleurs et petales, ete pâquerettes, automne feuilles
   * tombees, hiver paillettes de neige.
   */
  private drawSeasonDetails(ctx: CanvasRenderingContext2D, season: string, rand: () => number, wrapDraw: (draw: (ox: number, oy: number) => void) => void): void {
    const dot = (x: number, y: number, r: number, color: string): void => {
      wrapDraw((ox, oy) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x + ox, y + oy, r, 0, Math.PI * 2);
        ctx.fill();
      });
    };
    const leaf = (x: number, y: number, rx: number, ry: number, rot: number, color: string): void => {
      wrapDraw((ox, oy) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(x + ox, y + oy, rx, ry, rot, 0, Math.PI * 2);
        ctx.fill();
      });
    };
    const pick = <T,>(list: readonly T[]): T => list[Math.floor(rand() * list.length) % list.length] as T;
    if (season === 'spring') {
      const petals = ['#ffd1e3', '#ffffff', '#ffc2da'];
      for (let i = 0; i < 70; i++) leaf(rand() * GROUND_TEX, rand() * GROUND_TEX, 5, 2.6, rand() * Math.PI, pick(petals));
      const blooms = ['#ff9fc3', '#ffffff', '#ffe27a', '#c9a0ff'];
      for (let i = 0; i < 60; i++) {
        const x = rand() * GROUND_TEX;
        const y = rand() * GROUND_TEX;
        dot(x, y, 4.2, pick(blooms));
        dot(x, y, 1.6, '#ffd23c');
      }
    } else if (season === 'summer') {
      for (let i = 0; i < 46; i++) {
        const x = rand() * GROUND_TEX;
        const y = rand() * GROUND_TEX;
        dot(x, y, 3.6, '#ffffff');
        dot(x, y, 1.5, '#ffd23c');
      }
      for (let i = 0; i < 20; i++) dot(rand() * GROUND_TEX, rand() * GROUND_TEX, 2.6, '#ffe27a');
    } else if (season === 'autumn') {
      const colors = ['#d9772f', '#c24a3c', '#e8b23a', '#a8582a', '#f2c14e'];
      for (let i = 0; i < 90; i++) leaf(rand() * GROUND_TEX, rand() * GROUND_TEX, 7 + rand() * 3, 3.6 + rand() * 1.4, rand() * Math.PI, pick(colors));
    } else if (season === 'winter') {
      for (let i = 0; i < 70; i++) {
        const x = rand() * GROUND_TEX;
        const y = rand() * GROUND_TEX;
        dot(x, y, 1.8 + rand() * 1.4, '#ffffff');
      }
      for (let i = 0; i < 18; i++) dot(rand() * GROUND_TEX, rand() * GROUND_TEX, 3.2, '#cfe6f5');
    }
  }

  /** Texture de sol : fond uni, taches arrondies plus sombres, petites touffes claires (generee UNE fois par biome). */
  private makeGroundTexture(def: GroundLook): CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = GROUND_TEX;
    canvas.height = GROUND_TEX;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const rand = shapeRng(def.id.length * 7919 + def.ground.base.charCodeAt(1));
      ctx.fillStyle = def.ground.base;
      ctx.fillRect(0, 0, GROUND_TEX, GROUND_TEX);
      const wrapDraw = (draw: (ox: number, oy: number) => void): void => {
        for (const ox of [-GROUND_TEX, 0, GROUND_TEX]) for (const oy of [-GROUND_TEX, 0, GROUND_TEX]) draw(ox, oy);
      };
      ctx.fillStyle = def.ground.patch;
      for (let i = 0; i < 40; i++) {
        const x = rand() * GROUND_TEX;
        const y = rand() * GROUND_TEX;
        const rx = 18 + rand() * 46;
        const ry = rx * (0.55 + rand() * 0.4);
        const rot = rand() * Math.PI;
        wrapDraw((ox, oy) => {
          ctx.beginPath();
          ctx.ellipse(x + ox, y + oy, rx, ry, rot, 0, Math.PI * 2);
          ctx.fill();
        });
      }
      ctx.strokeStyle = def.ground.accent;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      for (let i = 0; i < 90; i++) {
        const x = rand() * GROUND_TEX;
        const y = rand() * GROUND_TEX;
        const sz = 5 + rand() * 6;
        wrapDraw((ox, oy) => {
          ctx.beginPath();
          ctx.moveTo(x + ox - sz, y + oy);
          ctx.lineTo(x + ox - sz * 0.3, y + oy - sz);
          ctx.moveTo(x + ox, y + oy);
          ctx.lineTo(x + ox, y + oy - sz * 1.3);
          ctx.moveTo(x + ox + sz, y + oy);
          ctx.lineTo(x + ox + sz * 0.3, y + oy - sz);
          ctx.stroke();
        });
      }
      if (def.id.startsWith('hub:')) this.drawSeasonDetails(ctx, def.id.slice(4), rand, wrapDraw);
      ctx.fillStyle = def.ground.accent;
      for (let i = 0; i < 28; i++) {
        const x = rand() * GROUND_TEX;
        const y = rand() * GROUND_TEX;
        wrapDraw((ox, oy) => {
          ctx.beginPath();
          ctx.arc(x + ox, y + oy, 2 + rand() * 2, 0, Math.PI * 2);
          ctx.fill();
        });
      }
    }
    const tex = new CanvasTexture(canvas);
    tex.wrapS = RepeatWrapping;
    tex.wrapT = RepeatWrapping;
    tex.repeat.set(GROUND_REPEAT, GROUND_REPEAT);
    tex.colorSpace = SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
  }
}

